import React from 'react';
import { useLocation, useSearch } from '@tanstack/react-router';
import {
  Box,
  Container,
  CircularProgress,
  Alert,
  Button,
  Typography,
  Snackbar,
  Paper,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import {
  findMenuItemByLink,
  findParentPathMenuItem,
  findAncestorMenuItem,
  useGetMenuItemsQuery,
  useGetMenuItemStructureQuery,
} from '../menu';
import { whenCommand, getCommandId } from './commands/utils';
import { GridType, type Command } from '../menu/types';
import {
  GridPageHeader,
  GridAlertContainer,
  GridDebugPanel,
  GridDialogs,
  GridWithCommands,
  type GridWithCommandsRef,
} from './components';
import { useDeleteEntityMutation, useRestoreEntityMutation } from './api/gridApi';
import { useGridPageLogic } from './hooks';
import { usePageHeader } from '../../contexts/PageHeaderContext';
import type { NavigationRequestOptions } from './types/navigation';
import { sxColFillHidden } from '../../theme/sxMixins';
import { resolveCustomGridPage } from './customGridPages';
import { resolveGridMetadataId, resolveGridPageTitle } from './gridPageResolution';

export type GridPageProps = {
  /** Umbraco grid node key from settings skips menu resolution */
  standaloneNodeKey?: string;
  /** Optional page header title for standalone grids */
  standaloneTitle?: string;
};

export const GridPage = (props: GridPageProps = {}) => {
  const { standaloneNodeKey, standaloneTitle } = props;
  const { pathname } = useLocation();
  const search = useSearch({ strict: false }) as {
    gridContentKey?: string;
    focusRowId?: string;
    startDate?: string;
    endDate?: string;
  };

  const { data: menuItems = [], isLoading: menuLoading } = useGetMenuItemsQuery(undefined);

  let menuItem = findMenuItemByLink(menuItems, pathname);
  const isChildGrid = !menuItem;
  if (!menuItem) {
    menuItem = findParentPathMenuItem(menuItems, pathname);
  }
  if (!menuItem) {
    menuItem = findAncestorMenuItem(menuItems, pathname);
  }
  const menuItemId = menuItem?.id;

  /** Grid without menu match: explicit key from route (e.g. /notifications) or ?gridContentKey= */
  const isStandaloneGridByKey = !!standaloneNodeKey || (!menuItem && !!search.gridContentKey);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);
  const [pendingSaveAfterDelete, setPendingSaveAfterDelete] = React.useState(false);
  const [isResetFiltersDialogOpen, setIsResetFiltersDialogOpen] = React.useState(false);
  const [deletingRow, setDeletingRow] = React.useState<Record<string, unknown> | null>(null);
  const [showDebug, setShowDebug] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const [deleteEntity, { isLoading: isDeleting }] = useDeleteEntityMutation();
  const [restoreEntity, { isLoading: isRestoring }] = useRestoreEntityMutation();

  const normalizedPathname = React.useMemo(
    () => (pathname.endsWith('/') ? pathname.slice(0, -1) : pathname),
    [pathname]
  );

  const preferCachedOnMount = React.useMemo<boolean>(() => {
    try {
      const key = `GRID_FAST_RETURN:${normalizedPathname}`;
      return sessionStorage.getItem(key) === '1';
    } catch {
      return false;
    }
  }, [normalizedPathname]);

  React.useEffect(() => {
    if (!preferCachedOnMount) return;
    try {
      const key = `GRID_FAST_RETURN:${normalizedPathname}`;
      sessionStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  }, [preferCachedOnMount, normalizedPathname]);

  const {
    data: structure,
    isLoading: structureLoading,
    error: structureError,
  } = useGetMenuItemStructureQuery(
    { menuItemId: menuItemId! },
    {
      skip: menuItemId == null || !!standaloneNodeKey || (!menuItem && !!search.gridContentKey),
    }
  );

  let childGridNodeKey: string | null = null;
  if (isChildGrid) {
    if (search.gridContentKey) {
      childGridNodeKey = search.gridContentKey;
    } else if (structure?.buttons) {
      const normPath = (p: string | null) => (p ? (p.endsWith('/') ? p.slice(0, -1) : p) : null);
      const currentPath = normPath(pathname);
      childGridNodeKey =
        structure.buttons.find((btn) => btn.link && normPath(btn.link) === currentPath)?.nodeKey ??
        null;
    }
  }

  const gridMetadataId = React.useMemo<number | null>(() => {
    return resolveGridMetadataId(isChildGrid, menuItem);
  }, [isChildGrid, menuItem]);

  const gridLogic = useGridPageLogic({
    menuItemId: isStandaloneGridByKey ? undefined : isChildGrid ? undefined : gridMetadataId!,
    nodeKey: isStandaloneGridByKey
      ? (standaloneNodeKey ?? search.gridContentKey!)
      : isChildGrid
        ? childGridNodeKey!
        : undefined,
    enableBatchOperations: true,
    enableNavigationGuard: true,
    preferCachedOnMount,
    initialDateRange:
      search.startDate && search.endDate
        ? { startDate: search.startDate, endDate: search.endDate }
        : undefined,
    skip: isStandaloneGridByKey
      ? !(standaloneNodeKey ?? search.gridContentKey)
      : isChildGrid
        ? !childGridNodeKey
        : gridMetadataId === null || gridMetadataId === undefined,
  });

  const metadata = gridLogic.metadata;
  const customGridPage = resolveCustomGridPage(metadata?.dataSource);
  const metadataLoading = gridLogic.metadataLoading;
  const metadataError = gridLogic.metadataError;
  const parsedColumns = gridLogic.parsedColumns;
  const resolvedGridTitle = resolveGridPageTitle({
    isStandaloneGridByKey,
    standaloneTitle,
    menuCaption: menuItem?.caption,
    metadataTitle: metadata?.title,
  });

  const navGuard = gridLogic.navGuard!;
  const requestNavigation = navGuard.requestNavigation;
  const hasHeaderSource = Boolean(menuItem || metadata);

  const handleHeaderNavigate = React.useCallback(
    (path: string, options?: NavigationRequestOptions) => {
      requestNavigation(path, options);
    },
    [requestNavigation]
  );

  // ── Header actions (Save / Revert / Import) ───────────────────────────────
  // Rendered by MainLayout's PageSubHeader on the same row as the page title,
  // via PageHeaderContext.headerActions — not as a standalone block above the grid.
  const gridWithCommandsRef = React.useRef<GridWithCommandsRef>(null);

  const saveCommand = React.useMemo(
    () => metadata?.commands?.find((c) => getCommandId(c) === 'save') ?? null,
    [metadata?.commands]
  );
  const revertCommand = React.useMemo(
    () => metadata?.commands?.find((c) => getCommandId(c) === 'revert') ?? null,
    [metadata?.commands]
  );
  const importCommand = React.useMemo(
    () => metadata?.commands?.find((c) => getCommandId(c) === 'import') ?? null,
    [metadata?.commands]
  );

  const handleConfirmResetFilters = React.useCallback(() => {
    gridLogic.gridFiltering.resetAllFilters();
    setIsResetFiltersDialogOpen(false);
  }, [gridLogic, setIsResetFiltersDialogOpen]);

  const { refetchGridData } = gridLogic;
  const handleRefresh = React.useCallback(() => {
    refetchGridData();
  }, [refetchGridData]);

  const handleHeaderSave = React.useCallback(() => {
    if (!saveCommand) return;

    if (gridLogic.markedForDeletion.size > 0) {
      setIsDeleteDialogOpen(true);
      setPendingSaveAfterDelete(true);
      return;
    }

    gridWithCommandsRef.current?.handleCommandClick(saveCommand);
  }, [
    saveCommand,
    gridLogic.markedForDeletion.size,
    setIsDeleteDialogOpen,
    setPendingSaveAfterDelete,
  ]);

  const { handleCancelAll } = gridLogic;
  const handleHeaderRevert = React.useCallback(() => {
    handleCancelAll();
  }, [handleCancelAll]);

  const handleHeaderImport = React.useCallback((cmd: Command) => {
    gridWithCommandsRef.current?.handleCommandClick(cmd);
  }, []);

  const headerActions = React.useMemo(
    () => (
      <GridPageHeader
        onRefresh={handleRefresh}
        showDebug={showDebug}
        onDebugToggle={() => setShowDebug((v) => !v)}
        inlineEditable={gridLogic.inlineEditable}
        hasUnsavedChanges={gridLogic.hasUnsavedChanges}
        isBatchSaving={gridLogic.isBatchSaving}
        onSave={handleHeaderSave}
        onRevert={handleHeaderRevert}
        saveCommand={saveCommand}
        revertCommand={revertCommand}
        importCommand={importCommand}
        onImportClick={handleHeaderImport}
      />
    ),
    [
      handleRefresh,
      showDebug,
      gridLogic.inlineEditable,
      gridLogic.hasUnsavedChanges,
      gridLogic.isBatchSaving,
      saveCommand,
      revertCommand,
      importCommand,
      handleHeaderSave,
      handleHeaderRevert,
      handleHeaderImport,
      setShowDebug,
    ]
  );

  const { setPageHeader, clearPageHeader } = usePageHeader();
  React.useEffect(() => {
    if (hasHeaderSource) {
      setPageHeader({
        title: resolvedGridTitle,
        navButtons:
          isStandaloneGridByKey || (structure?.buttons?.length ?? 0) <= 1
            ? undefined
            : structure?.buttons,
        onNavigate: handleHeaderNavigate,
        headerActions,
      });
    }
    return () => clearPageHeader();
  }, [
    hasHeaderSource,
    resolvedGridTitle,
    structure?.buttons,
    isStandaloneGridByKey,
    setPageHeader,
    clearPageHeader,
    handleHeaderNavigate,
    headerActions,
  ]);

  const handleNavigate = React.useCallback(
    (path: string | number, options?: NavigationRequestOptions) => {
      requestNavigation(typeof path === 'number' ? (path as -1) : path, options);
    },
    [requestNavigation]
  );

  const handleEdit = React.useCallback(
    (row: Record<string, unknown>) => {
      const isGridWithDetail = metadata?.gridType === GridType.GridWithDetail;

      if (isGridWithDetail) {
        const rowId = String(row.Id);
        const cleanPathname = pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
        const editPath = `${cleanPathname}/${rowId}`;
        requestNavigation(editPath, {
          search: {
            gridContentKey: metadata.contentKey,
          },
        });
      }
    },
    [metadata, pathname, requestNavigation]
  );

  const handleDelete = (row: Record<string, unknown>) => {
    const isInlineEditable = gridLogic.inlineEditable;

    if (!isInlineEditable) {
      setDeletingRow(row);
      setIsDeleteDialogOpen(true);
    } else {
      gridLogic.handleInlineRowDelete(row);
    }
  };

  const handleRestore = React.useCallback(
    async (row: Record<string, unknown>) => {
      if (!row.Id || !metadata?.contentKey) return;

      try {
        await restoreEntity({
          id: String(row.Id),
          nodeKey: metadata.contentKey,
        }).unwrap();

        gridLogic.refetchGridData();
      } catch (error) {
        console.error('Failed to restore entity:', error);
        throw error;
      }
    },
    [metadata, restoreEntity, gridLogic]
  );

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setDeletingRow(null);
    setPendingSaveAfterDelete(false);
  };

  const handleConfirmDelete = async () => {
    if (pendingSaveAfterDelete) {
      handleCloseDeleteDialog();
      if (saveCommand) gridWithCommandsRef.current?.handleCommandClick(saveCommand);
      return;
    }

    if (!deletingRow || !deletingRow.Id || !metadata?.contentKey) {
      return;
    }

    try {
      await deleteEntity({
        id: String(deletingRow.Id),
        nodeKey: metadata.contentKey,
      }).unwrap();

      handleCloseDeleteDialog();
      gridLogic.refetchGridData();
    } catch (error) {
      console.error('Failed to delete entity:', error);
      handleCloseDeleteDialog();
      setSnackbar({
        open: true,
        message: `Delete failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        severity: 'error',
      });
    }
  };

  const formatError = (error: unknown): string => {
    if (!error) return '';
    if (typeof error !== 'object') return String(error);
    if ('status' in error) {
      return 'error' in error ? String(error.error) : `Error: ${String(error.status)}`;
    }
    if ('message' in error && typeof error.message === 'string') return error.message;
    return String(error);
  };

  const metadataErrorMsg = metadataError ? formatError(metadataError) : '';
  const structureErrorMsg = structureError ? formatError(structureError) : '';

  if (menuLoading || metadataLoading || (!isStandaloneGridByKey && structureLoading)) {
    return (
      <Container maxWidth={false} sx={{ ...sxColFillHidden, height: '100%' }}>
        <Box
          sx={{
            ...sxColFillHidden,
            p: 1,
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <CircularProgress size={48} />
          <Typography variant="body2" color="text.secondary">
            Loading grid configuration...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (!menuItem && !isStandaloneGridByKey) {
    return (
      <Container maxWidth={false}>
        <Box sx={{ mt: 1 }}>
          <Alert severity="warning">Page not found: {pathname}</Alert>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navGuard.requestNavigation('/dashboard')}
            sx={{ mt: 1.5 }}
          >
            Go to Dashboard
          </Button>
        </Box>
      </Container>
    );
  }

  if (metadataError || (!isStandaloneGridByKey && structureError)) {
    return (
      <Container maxWidth={false}>
        <Box sx={{ mt: 1 }}>
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={handleRefresh}>
                Retry
              </Button>
            }
          >
            {metadataErrorMsg || structureErrorMsg}
          </Alert>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navGuard.requestNavigation(-1)}
            sx={{ mt: 1.5 }}
          >
            Back
          </Button>
        </Box>
      </Container>
    );
  }

  if (!metadata) {
    return (
      <Container maxWidth={false}>
        <Box sx={{ mt: 1 }}>
          <Alert severity="info">No grid metadata available for this menu item.</Alert>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navGuard.requestNavigation(-1)}
            sx={{ mt: 1.5 }}
          >
            Back
          </Button>
        </Box>
      </Container>
    );
  }

  if (customGridPage) {
    return customGridPage;
  }

  const overlayLoading =
    (gridLogic.dataFetching && !gridLogic.dataLoading) ||
    gridLogic.isBatchSaving ||
    isDeleting ||
    isRestoring;

  return (
    <Container maxWidth={false} disableGutters sx={{ ...sxColFillHidden, height: '100%' }}>
      <Box sx={{ ...sxColFillHidden, p: 1 }}>
        <Paper
          elevation={0}
          sx={{
            ...sxColFillHidden,
            p: { xs: 2, md: 3 },
            backgroundColor: 'background.paper',
            borderRadius: '4px',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <GridAlertContainer
            gridError={gridLogic.dataError}
            parsedColumnsCount={parsedColumns.length}
            onRetryGrid={handleRefresh}
          />

          <GridWithCommands
            ref={gridWithCommandsRef}
            key={metadata.contentKey}
            focusRowId={search.focusRowId}
            metadata={metadata}
            pageTitle={resolvedGridTitle}
            columns={parsedColumns}
            commands={metadata?.commands}
            data={gridLogic.displayData}
            gridData={gridLogic.gridData}
            lookupMaps={gridLogic.lookupMapsAsMap}
            filterLookupMaps={gridLogic.filterLookupMapsAsMap}
            rawLookupItems={gridLogic.rawLookupItemsAsMap}
            configurationTables={gridLogic.configurationTables}
            dropdownBaseFilter={gridLogic.dropdownBaseFilter}
            totalCount={gridLogic.totalCount}
            serverSummary={gridLogic.serverSummary}
            page={gridLogic.gridFiltering.pageIndex}
            pageSize={gridLogic.gridFiltering.pageSize}
            onPageChange={gridLogic.gridFiltering.handlePageChange}
            onPageSizeChange={gridLogic.gridFiltering.handlePageSizeChange}
            onSortChange={gridLogic.gridFiltering.handleSortChange}
            onFilterChange={gridLogic.gridFiltering.handleFilterChange}
            onDateRangeChange={gridLogic.gridFiltering.handleDateRangeChange}
            onEdit={metadata.gridType === GridType.GridWithDetail ? handleEdit : undefined}
            onDelete={
              whenCommand(metadata.commands, 'delete', handleDelete) ? handleDelete : undefined
            }
            onRestore={whenCommand(metadata.commands, 'showdeleted', handleRestore)}
            onDataUpdate={gridLogic.handleDataUpdate}
            onRowDataUpdate={gridLogic.handleRowDataUpdate}
            modifiedCells={gridLogic.modifiedCells}
            markedForDeletion={gridLogic.markedForDeletion}
            hasUnsavedChanges={gridLogic.hasUnsavedChanges}
            isBatchSaving={gridLogic.isBatchSaving}
            modifiedRows={gridLogic.modifiedRows}
            onRollbackRow={gridLogic.handleRollbackRow}
            onRefresh={handleRefresh}
            onSave={gridLogic.handleBatchSave}
            onCancel={gridLogic.handleCancelAll}
            onAddRow={gridLogic.handleAddNewRow}
            onAddRowWithData={gridLogic.handleAddNewRowWithData}
            onResetFilters={() => setIsResetFiltersDialogOpen(true)}
            onToggleGridVisibilityFilter={gridLogic.toggleGridVisibilityFilter}
            commandCaptionOverrides={gridLogic.commandCaptionOverrides}
            filterResetTrigger={gridLogic.gridFiltering.filterResetTrigger}
            navigate={handleNavigate}
            currentPath={pathname}
            loading={gridLogic.dataLoading}
            overlayLoading={overlayLoading}
            nodeKey={metadata.contentKey}
            inlineEditable={gridLogic.inlineEditable}
            searchTerm={gridLogic.gridFiltering.searchTerm}
            onSearchChange={gridLogic.gridFiltering.handleSearchChange}
            validationErrors={gridLogic.validationErrors}
            showDeleted={gridLogic.showDeleted}
            onShowDeletedToggle={gridLogic.handleShowDeletedToggle}
            initialSorting={gridLogic.initialSortingForTable}
            initialColumnFilters={gridLogic.initialColumnFiltersForTable}
            columnFiltersSessionKey={gridLogic.columnFiltersSessionKey}
            savedColumnVisibility={
              gridLogic.persistedStateFetching
                ? undefined
                : gridLogic.persistedState?.columnVisibility
            }
            savedColumnSizing={
              gridLogic.persistedStateFetching ? undefined : gridLogic.persistedState?.columnSizing
            }
            savedColumnOrder={
              gridLogic.persistedStateFetching ? undefined : gridLogic.persistedState?.columnOrder
            }
            onSaveGridState={gridLogic.handleSaveGridState}
            onClearGridState={gridLogic.handleClearGridState}
            dateRange={gridLogic.dateRange}
            headerActionsExtracted
            maxHeight="100%"
          />
        </Paper>

        <GridDebugPanel columns={metadata.columns} metadata={metadata} visible={showDebug} />

        <GridDialogs
          deleteDialogOpen={isDeleteDialogOpen}
          onCloseDeleteDialog={handleCloseDeleteDialog}
          onConfirmDelete={handleConfirmDelete}
          isDeleting={isDeleting}
          deleteTitle={pendingSaveAfterDelete ? 'Confirm deletion' : undefined}
          deleteMessage={
            pendingSaveAfterDelete
              ? `${gridLogic.markedForDeletion.size} row(s) marked for deletion will be permanently deleted. Continue?`
              : undefined
          }
          unsavedDialogOpen={navGuard.isUnsavedChangesDialogOpen}
          onCloseUnsaved={navGuard.handleCancelNavigation}
          onDiscardChanges={navGuard.handleDiscardAndNavigate}
          onSaveChanges={navGuard.handleSaveAndNavigate}
          savingChanges={gridLogic.isBatchSaving}
          saveError={navGuard.saveBeforeNavigateError}
          resetFiltersDialogOpen={isResetFiltersDialogOpen}
          onCloseResetFiltersDialog={() => setIsResetFiltersDialogOpen(false)}
          onConfirmResetFilters={handleConfirmResetFilters}
        />

        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={(_event, reason) => {
            if (reason === 'clickaway') return;
            setSnackbar((prev) => ({ ...prev, open: false }));
          }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default GridPage;
